import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DevisStatut, IDevisCreateRequest, IDevisRequest, IDevisResponse } from 'src/app/shared/models/devis';
import { IApplication } from 'src/app/shared/models/fiche-expression-besoin';
import { UserProfile } from 'src/app/shared/models/user-profile';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import { UtilsService } from 'src/app/shared/services/utils.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DevisService {

  private createAuditHeaders(): HttpHeaders {
    const currentUser = this.currentUserService.getCurrentUser() as any || {};

    const requestId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const role = currentUser?.role ?? '';
    const displayName = currentUser.displayName ?? `${currentUser.firstName ?? ''} ${currentUser.lastName ?? ''}`.trim();

    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'X-User-Identifier': currentUser.identifier ?? '',
      'X-User-Display-Name': displayName,
      'X-User-Role': role,
      'X-Actor-Source': 'FE',
      'X-Request-ID': requestId
    });
  }

  get options() {
    return {
      headers: this.createAuditHeaders()
    };
  }

  constructor(
    private http: HttpClient,
    private utilsService: UtilsService,
    private authenticationService: AuthenticationService,
    private currentUserService: CurrentUserService
  ) { }

  getAll(): Observable<IDevisResponse[]> {
    return this.http.get<IDevisResponse[]>(environment.backendApiUrl + '/devis', this.options)
      .pipe(catchError(this.utilsService.handleError));
  }

  getById(id: string): Observable<IDevisResponse> {
    return this.http.get<IDevisResponse>(environment.backendApiUrl + '/devis/' + id, this.options)
      .pipe(catchError(this.utilsService.handleError));
  }

  getByStatut(statut: DevisStatut): Observable<IDevisResponse[]> {
    return this.http.get<IDevisResponse[]>(
      environment.backendApiUrl + '/devis/by-statut?statut=' + statut,
      this.options
    ).pipe(catchError(this.utilsService.handleError));
  }

  getByNonStatut(statut: DevisStatut): Observable<IDevisResponse[]> {
    return this.http.get<IDevisResponse[]>(
      environment.backendApiUrl + '/devis/by-non-statut?statut=' + statut,
      this.options
    ).pipe(catchError(this.utilsService.handleError));
  }

  getByApplications(applications: IApplication[]): Observable<IDevisResponse[]> {
    return this.http.post<IDevisResponse[]>(environment.backendApiUrl + '/devis/by-applications', { applications }, this.options)
      .pipe(catchError(this.utilsService.handleError));
  }


getByApplicationsAndNonStatus(
  applications: IApplication[],
  statut: DevisStatut
): Observable<IDevisResponse[]> {

  return this.http.post<IDevisResponse[]>(
    `${environment.backendApiUrl}/devis/by-applications-and-non-status`,
    { applications },
    {
      ...this.options,
      params: {
        statut: statut.toString()
      }
    }
  ).pipe(
    catchError(this.utilsService.handleError)
  );
}

  create(request: IDevisCreateRequest): Observable<IDevisResponse> {
    return this.http.post<IDevisResponse>(
      environment.backendApiUrl + '/devis',
      request,
      this.options
    ).pipe(catchError(this.utilsService.handleError));
}

  update(id: string, request: IDevisRequest): Observable<IDevisResponse> {
    return this.http.put<IDevisResponse>(environment.backendApiUrl + '/devis/' + id, request, this.options)
      .pipe(catchError(this.utilsService.handleError));
  }

  updateStatut(
    id: string,
    statut: DevisStatut,
    motifRefus?: string,
    numeroBdc?: string,
    dateFinTravaux?: string,
    motifRetourArriere?: string,
    userRole?: string
  ): Observable<IDevisResponse> {
    let url = environment.backendApiUrl + '/devis/' + id + '/statut?statut=' + statut;
    if (motifRefus) url += '&motifRefus=' + encodeURIComponent(motifRefus);
    if (numeroBdc) url += '&numeroBdc=' + encodeURIComponent(numeroBdc);
    if (dateFinTravaux) url += '&dateFinTravaux=' + encodeURIComponent(dateFinTravaux);
    if (motifRetourArriere) url += '&motifRetourArriere=' + encodeURIComponent(motifRetourArriere);
    if (userRole) url += '&userRole=' + encodeURIComponent(userRole);
    return this.http.patch<IDevisResponse>(url, {}, this.options)
      .pipe(catchError(this.utilsService.handleError));
  }

  updateFebStatut(febId: string, statut: string): Observable<any> {
  return this.http.put<any>(
    `${environment.backendApiUrl}/febs/${febId}?statut=${statut}`,
    {},
    this.options
  ).pipe(catchError(this.utilsService.handleError));
}


  delete(id: string): Observable<any> {
    return this.http.delete<any>(environment.backendApiUrl + '/devis/' + id, this.options)
      .pipe(catchError(this.utilsService.handleError));
  }

  createFromFeb(febId: string): Observable<IDevisResponse> {
    return this.http.post<IDevisResponse>(
      environment.backendApiUrl + '/devis/from-feb/' + febId,
      {},
      this.options
    ).pipe(catchError(this.utilsService.handleError));
  }
  exportDevisData(devisIds: string[]): Observable<Blob> {
    return this.http.post(environment.backendApiUrl + '/devis/export/', {
      devisIds
    }, { ...this.options, responseType: 'blob' }).pipe(
      catchError((error: HttpErrorResponse) => error.status === 403
        ? throwError(error) : this.utilsService.handleError(error))
    );
  }

 getNumContrat(): Observable<string> {
  return this.http.get(
    environment.backendApiUrl + '/numcontrat',
    { ...this.options, responseType: 'text' }
  ).pipe(catchError(this.utilsService.handleError));
}

getNomContrat(): Observable<string> {
  return this.http.get(
    environment.backendApiUrl + '/nomcontrat',
    { ...this.options, responseType: 'text' }
  ).pipe(catchError(this.utilsService.handleError));
}

getDateEnvoiSncf(id: string): Observable<{ dateEnvoiSncf: string | null }> {
  return this.http.get<{ dateEnvoiSncf: string | null }>(
    `${environment.backendApiUrl}/audit/${id}/date-envoi-sncf`
  );
}
}
